<template>
  <div class="invigilate-playback">
    <div class="invigilate-playback_title">监考视频回放</div>
    <div class="player-wrap">
      <video
        ref="videoEl"
        class="video"
        controls
        webkit-playsinline
        playsinline
        x5-playsinline
        :poster="poster"
      ></video>
      <div class="player-toolbar">
        <div class="left">
          <span class="btn" @click="playPrev" :class="{ disabled: currentSegmentIndex <= 0 }">上一段</span>
          <span class="btn" @click="togglePlay">{{ isPlaying ? '暂停' : '播放' }}</span>
          <span class="btn" @click="playNext" :class="{ disabled: currentSegmentIndex >= segments.length - 1 }">下一段</span>
        </div>
        <div class="center">
          <span class="time">{{ formatSec(globalCurrentTimeSec) }} / {{ formatSec(totalDurationSec) }}</span>
        </div>
        <div class="right">
          <span class="label">自动连播</span>
          <el-switch v-model="autoContinue" size="mini" />
        </div>
      </div>
    </div>

    <div class="timeline" @click="onTimelineClick($event)">
      <div class="timeline-track"></div>
      <div
        v-for="(seg, idx) in segments"
        :key="idx"
        class="timeline-segment"
        :style="segmentStyle(seg)"
        :title="segmentTitle(seg)"
      ></div>
      <div
        v-for="(mk, idx) in filteredCheatMarkers"
        :key="'mk-' + idx"
        class="timeline-marker"
        :style="markerStyle(mk)"
        :title="mk.label"
      ></div>
      <div class="timeline-cursor" :style="cursorStyle"></div>
    </div>

    <template v-if="cheatMarkers.length">
      <div class="invigilate-playback_title">存疑列表说明</div>
      <div class="doubt-list" >
        <div class="doubt-item" v-for="(item, idx) in cheatMarkers" :key="idx" @click="onClickDoubt(item)">
          <span class="doubt-text">序号：{{ idx + 1 }}</span>
          <span class="doubt-text">存疑事件：{{ item.label }}</span>
          <span class="doubt-text">视频位置：{{ formatSec(item.time) }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { CheatTypeList } from '@/common/constant/qz';
import Hls from 'hls.js';
import { Switch } from 'element-ui';

export default {
  name: 'InvigilatePlayback',
  components: { 'el-switch': Switch },
  props: {
    // 秒
    startTimeSec: { type: Number, required: true },
    endTimeSec: { type: Number, required: true },
    // [{ m3u8, startTimeSec, endTimeSec }]
    segmentList: { type: Array, default: () => [] },
    // 作弊标记: [{ type, timeMs }]
    cheats: { type: Array, default: () => [] },
    poster: { type: String, default: '' },
  },
  data() {
    return {
      hls: null,
      isPlaying: false,
      autoContinue: true,
      currentSegmentIndex: 0,
      // 当前段内播放时间（秒）
      currentTimeSecInSegment: 0,
      cheatTypeMap: {
        personExit: '人员离开',
        actionPoseStandup: '起身',
        CALL: '通话',
        HAT: '帽子遮挡',
      },
    };
  },
  computed: {
    totalDurationSec() {
      return Math.max(0, (this.endTimeSec || 0) - (this.startTimeSec || 0));
    },
    segments() {
      const list = (this.segmentList || [])
        .slice()
        .sort((a, b) => a.startTimeSec - b.startTimeSec)
        .map(seg => {
          const absStartSec = Number(seg.startTimeSec || 0);
          const absEndSec = Number(seg.endTimeSec || 0);
          let relStartSec = Math.max(0, absStartSec - (this.startTimeSec || 0));
          let relEndSec = Math.max(0, absEndSec - (this.startTimeSec || 0));
          // clamp into [0, totalDurationSec]
          relStartSec = Math.min(relStartSec, this.totalDurationSec);
          relEndSec = Math.min(Math.max(relEndSec, relStartSec), this.totalDurationSec);
          return {
            m3u8: seg.m3u8,
            relStartSec,
            relEndSec,
            durationSec: Math.max(0, relEndSec - relStartSec),
          };
        });
      return list;
    },
    cheatMarkers() {
      // 标准化为相对秒，且裁剪到总时长范围
      return (this.cheats || [])
        .filter(m => typeof m.timeMs === 'number' || typeof m.timeSec === 'number')
        .map(m => {
          const absTimeSec = typeof m.timeSec === 'number' ? Number(m.timeSec) : Number(m.timeMs) / 1000;
          let rel = Math.max(0, absTimeSec - (this.startTimeSec || 0));
          rel = Math.min(rel, this.totalDurationSec);
          return { type: m.type, time: rel, label: (this.CheatTypeListItem && this.CheatTypeListItem(m.type)) || m.label || this.cheatTypeMap[m.type] || m.type };
        });
    },
    filteredCheatMarkers() {
      // 过滤：相邻标记至少间隔 60 秒
      const minGap = 120; // 秒
      const filtered = [];
      let lastKeptTime = -Infinity;
      for (let i = 0; i < this.cheatMarkers.length; i += 1) {
        const mk = this.cheatMarkers[i];
        if (mk.time - lastKeptTime >= minGap) {
          filtered.push(mk);
          lastKeptTime = mk.time;
        }
      }
      return filtered;
    },
    currentSegment() {
      return this.segments[this.currentSegmentIndex] || null;
    },
    globalCurrentTimeSec() {
      const seg = this.currentSegment;
      if (!seg) return 0;
      const rel = (seg.relStartSec || 0) + (this.currentTimeSecInSegment || 0);
      return Math.min(this.totalDurationSec, Math.max(0, rel));
    },
    cursorStyle() {
      const percent = this.totalDurationSec > 0 ? (this.globalCurrentTimeSec / this.totalDurationSec) * 100 : 0;
      return { left: `${Math.min(100, Math.max(0, percent))}%` };
    },
    // 存疑说明由外层模板直接展示 cheatMarkers，时间为相对秒
  },
  watch: {
    segmentList: {
      handler() {
        this.initFirstPlayable();
      },
      immediate: true,
    }
  },
  mounted() {
    this.initFirstPlayable();
  },
  beforeDestroy() {
    this.destroyHls();
  },
  methods: {
    // 可选：提供类型映射获取函数，供 cheatMarkers 生成 label 使用
    CheatTypeListItem(type) {
      const item = (CheatTypeList || []).find(it => it.value === type);
      return item ? item.label : null;
    },
    destroyHls() {
      try {
        if (this.hls) {
          this.hls.destroy();
        }
      } catch (e) { /* no-op */ }
      this.hls = null;
    },
    loadSegment(seg, autoplay = false) {
      if (!seg) return;
      const video = this.$refs.videoEl;
      if (!video) return;

      this.destroyHls();
      const src = this.normalizeM3u8(seg.m3u8);
      if (Hls.isSupported()) {
        console.log('hls supported, load source', seg)
        const hls = new Hls();
        this.hls = hls;
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, function() {
          video.play();
        });
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = src;
        video.addEventListener('loadedmetadata', function() {
          video.play();
        });
      }
      // video.src = seg.m3u8;
      // video.load();
      this.bindVideoEvents();
      this.currentTimeSecInSegment = 0;
      this.isPlaying = !!autoplay;
    },
    bindVideoEvents() {
      const video = this.$refs.videoEl;
      video.onplay = () => { this.isPlaying = true; };
      video.onpause = () => { this.isPlaying = false; };
      video.ontimeupdate = () => {
        this.currentTimeSecInSegment = video.currentTime || 0;
      };
      video.onended = () => {
        if (this.autoContinue) {
          this.playNext();
        } else {
          this.isPlaying = false;
        }
      };
    },
    initFirstPlayable() {
      if (!this.segments.length) return;
      this.currentSegmentIndex = 0;
      this.loadSegment(this.segments[0], false);
    },
    togglePlay() {
      const video = this.$refs.videoEl;
      if (!video) return;
      if (this.isPlaying) {
        video.pause();
      } else {
        video.play();
      }
    },
    playPrev() {
      if (this.currentSegmentIndex <= 0) return;
      this.currentSegmentIndex -= 1;
      this.loadSegment(this.segments[this.currentSegmentIndex], true);
    },
    playNext() {
      if (this.currentSegmentIndex >= this.segments.length - 1) return;
      this.currentSegmentIndex += 1;
      this.loadSegment(this.segments[this.currentSegmentIndex], true);
    },
    // 时间轴映射（使用相对秒）
    segmentStyle(seg) {
      const leftPct = this.totalDurationSec > 0 ? (seg.relStartSec / this.totalDurationSec) * 100 : 0;
      const widthPct = this.totalDurationSec > 0 ? (seg.durationSec / this.totalDurationSec) * 100 : 0;
      return { left: `${Math.max(0, leftPct)}%`, width: `${Math.max(0.5, widthPct)}%` };
    },
    markerStyle(mk) {
      const leftPct = this.totalDurationSec > 0 ? (mk.time / this.totalDurationSec) * 100 : 0;
      return { left: `${Math.min(100, Math.max(0, leftPct))}%` };
    },
    segmentTitle(seg) {
      const s = this.formatSec(seg.relStartSec);
      const e = this.formatSec(seg.relEndSec);
      return `${s} ~ ${e}`;
    },
    onTimelineClick(e) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const targetSecFromStart = percent * this.totalDurationSec; // 相对整体起点（秒）
      const targetRelSec = Math.max(0, Math.min(this.totalDurationSec, targetSecFromStart));
      this.seekToRelativeSec(targetRelSec);
    },
    // 新增：点击存疑列表项，跳转播放到对应相对时间（秒）
    onClickDoubt(item) {
      if (!item || typeof item.time !== 'number') return;
      const time = item.time - 5; // 往前5s
      this.seekToRelativeSec(time);
    },
    // 新增：按相对时间跳转并播放
    seekToRelativeSec(targetRelSec) {
      const clamped = Math.max(0, Math.min(this.totalDurationSec, Number(targetRelSec) || 0));
      const idx = this.segments.findIndex(seg => clamped >= seg.relStartSec && clamped <= seg.relEndSec);
      if (idx >= 0) {
        if (idx !== this.currentSegmentIndex) {
          this.currentSegmentIndex = idx;
          this.loadSegment(this.segments[idx], true);
          // 等待元数据后再跳转时间点，避免少量情况下跳转被覆盖
          this.$nextTick(() => {
            const video = this.$refs.videoEl;
            const seg = this.segments[idx];
            const offsetSec = clamped - seg.relStartSec;
            if (video && !isNaN(offsetSec)) {
              video.currentTime = Math.max(0, offsetSec);
              video.play();
            }
          });
        } else {
          const video = this.$refs.videoEl;
          const seg = this.segments[idx];
          const offsetSec = clamped - seg.relStartSec;
          if (video && !isNaN(offsetSec)) {
            video.currentTime = Math.max(0, offsetSec);
            video.play();
          }
        }
      } else {
        const nextIdx = this.segments.findIndex(seg => seg.relStartSec > clamped);
        const targetIdx = nextIdx >= 0 ? nextIdx : this.segments.length - 1;
        this.currentSegmentIndex = targetIdx;
        this.loadSegment(this.segments[targetIdx], true);
      }
    },
    // 工具方法
    findSegmentByTimeSec(timeSec) {
      return this.segments.find(seg => timeSec >= seg.relStartSec && timeSec <= seg.relEndSec);
    },
    formatSec(sec) {
      const totalSec = Math.floor(sec);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      const hh = h.toString().padStart(2, '0');
      const mm = m.toString().padStart(2, '0');
      const ss = s.toString().padStart(2, '0');
      return `${hh}:${mm}:${ss}`;
    },
    // 新增：m3u8 链接规范化，将 http:// 前缀改为 //
    normalizeM3u8(url) {
      if (typeof url !== 'string') return url;
      return url.replace(/^http:\/\//, '//');
    }
  }
}
</script>

<style scoped lang="less">
.invigilate-playback {
  text-align: left;
  &_title {
    color: var(--color-text-1, #000000E5);
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 16px;
  }
  .player-wrap {
    background: #f5f6f7;
    border-radius: 8px;
    padding: 12px;
    .video {
      width: 100%;
      height: 420px;
      background: #dcdfe6;
      border-radius: 6px;
    }
    .player-toolbar {
      margin-top: 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      .left, .center, .right { display: flex; align-items: center; }
      .btn {
        background: #fff;
        border: 1px solid #d9d9d9;
        padding: 4px 10px;
        border-radius: 4px;
        margin-right: 8px;
        cursor: pointer;
        &.disabled { opacity: 0.4; cursor: not-allowed; }
      }
      .time { color: #4e5969; font-size: 12px; }
      .label { color: #4e5969; font-size: 12px; margin-right: 6px; }
    }
  }
  .timeline {
    position: relative;
    height: 56px;
    margin-top: 12px;
    user-select: none;
    .timeline-track {
      position: absolute;
      left: 0; right: 0; top: 24px;
      height: 8px; border-radius: 4px; background: #ebeef5;
    }
    .timeline-segment {
      position: absolute; top: 24px; height: 8px;
      background: #409eff; border-radius: 4px;
    }
    .timeline-marker {
      position: absolute;
      top: 17px;
      transform: translateX(-50%);
      width: 20px;
      height: 20px;
      background: url("@/common/images/ic_track_warning.png") center / contain no-repeat;
    }
    .timeline-cursor {
      position: absolute;
      top: 17px;
      width: 20px;
      height: 20px;
      padding: 3px;
      box-sizing: border-box;
      background: #BD9F6833;
      border-radius: 50%;
      transform: translateX(-50%);
      &::after {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: #BD9F68;
      }
    }
  }
  .doubt-list {
    margin-top: 16px;
    .doubt-title {
      font-weight: 600;
      color: #1D2129;
      margin-bottom: 12px;
    }
    .doubt-item {
      background: #f5f6f7;
      border-radius: 4px;
      padding: 12px 16px;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      cursor: pointer;
      .doubt-text {
        color: #4e5969;
        font-size: 12px;
        margin-right: 20px;
        &:nth-child(1) {
          width: 80px;
        }
        &:nth-child(2) {
          width: 200px;
        }
      }
    }
  }
}
</style>